import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IconControl, Loading, PanelControl, TabPaneControl } from "spark-core-dx/components";
import { useCoreRoute, useGenericManager } from "spark-core-dx/hooks";
import AIPanel from "../../../components/AIChat/AIPanel";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import "./migrationProjectManage.scss";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "source", label: "Source Requirements" },
  { key: "destination", label: "Destination Readiness" },
  { key: "mapping", label: "Mapping Candidates" },
];

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value.toArray instanceof Function) return value.toArray();
  if (typeof value[Symbol.iterator] === "function") return Array.from(value);
  if (value.first instanceof Function) return [value.first()].filter(Boolean);
  return [];
};

const firstItem = (result) => {
  const normalized = Array.isArray(result) ? result[0] : result;
  const items = asArray(normalized?.Items);
  return items[0] ?? null;
};

const getId = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value) return String(value);
  }
  return "";
};

const getAnalysisId = (analysis) => getId(analysis, "AnalysisId", "analysisId", "_id");

const formatAnalysisSourceType = (value) => {
  const sourceType = String(value || "").trim();
  if (!sourceType) return "Analysis";
  if (sourceType.toLowerCase() === "initialrun") return "Initial";
  if (sourceType.toLowerCase() === "approvedplanrefinement") return "Refinement";
  return sourceType;
};

const getProjectAnalyses = (project) => asArray(project?.Analyses || project?.analyses)
  .filter(Boolean)
  .sort((left, right) => {
    const rightVersion = Number(right?.Version ?? right?.version ?? 0);
    const leftVersion = Number(left?.Version ?? left?.version ?? 0);
    if (rightVersion !== leftVersion) return rightVersion - leftVersion;
    return new Date(right?.GeneratedDate || right?.generatedDate || 0) - new Date(left?.GeneratedDate || left?.generatedDate || 0);
  });

const getDefaultAnalysisId = (project) => {
  const analyses = getProjectAnalyses(project);
  const currentAnalysisId = getId(project, "CurrentAnalysisId", "currentAnalysisId");
  if (currentAnalysisId && analyses.some((analysis) => getAnalysisId(analysis) === currentAnalysisId)) {
    return currentAnalysisId;
  }
  return getAnalysisId(analyses[0]);
};

const getRoutePath = (route) => {
  if (!route) return "";
  if (typeof route === "string") return route;
  return route.RoutePath || route.routePath || "";
};

const getPanelText = (value) => String(value ?? "").trim();

const normalizePanelParams = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value).reduce((result, [key, entry]) => {
    const text = getPanelText(entry);
    if (key && text) result[key] = text;
    return result;
  }, {});
};

const appendPathId = (base, id) => `${String(base || "").replace(/\/?$/, "/")}${id}`;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatPercent = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "-";
  return `${Math.round(numeric * 100)}%`;
};

const normalizeSeverity = (value) => {
  const text = String(value || "Info").toLowerCase();
  if (text === "error") return "error";
  if (text === "warning") return "warning";
  return "info";
};

const findingTitle = (finding) => finding?.Title || finding?.title || "Finding";
const findingDetail = (finding) => finding?.Detail || finding?.detail || "";
const findingRecommendation = (finding) => finding?.Recommendation || finding?.recommendation || "";
const findingSeverity = (finding) => finding?.Severity || finding?.severity || "Info";
const findingCategory = (finding) => finding?.Category || finding?.category || "";

const resultMessage = (result) => {
  if (!result) return "";
  if (typeof result.Value === "string") return result.Value;
  if (typeof result.value === "string") return result.value;
  if (result.Value?.message) return result.Value.message;
  if (result.Value?.Message) return result.Value.Message;
  if (result.value?.message) return result.value.message;
  if (result.value?.Message) return result.value.Message;
  return result.Message?.DisplayMessage || result.message?.displayMessage || result.DisplayMessage || result.displayMessage || "";
};

const MigrationProjectManage = ({
  panelMode = false,
  panelOpen = true,
  panelId = null,
  panelParams = null,
  onPanelClose = null,
}) => {
  const { migrationProjectId: routeMigrationProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const resolvedPanelParams = useMemo(() => normalizePanelParams(panelParams), [panelParams]);
  const migrationProjectId = panelMode
    ? (getPanelText(panelId) || getPanelText(searchParams.get("aiPanelId")) || routeMigrationProjectId)
    : routeMigrationProjectId;
  const { coreNavigate, getRoute } = useCoreRoute();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const migrationProjectMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.MigrationProject);
  const sourceSystemMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.SourceSystem);
  const sourceObjectMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.SourceObject);
  const externalSystemMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.ExternalSystem);
  const entityObjectMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.EntityObject);
  const managerRef = useRef({
    migrationProjectMgr,
    sourceSystemMgr,
    sourceObjectMgr,
    externalSystemMgr,
    entityObjectMgr,
  });

  const [project, setProject] = useState(null);
  const [sourceSystem, setSourceSystem] = useState(null);
  const [externalSystem, setExternalSystem] = useState(null);
  const [sourceObjects, setSourceObjects] = useState([]);
  const [entityObjects, setEntityObjects] = useState([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState("");
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const loadStateRef = useRef({ key: "", inFlight: false });
  const pendingSelectedAnalysisIdRef = useRef("");

  const analyses = useMemo(() => getProjectAnalyses(project), [project]);
  const analysis = useMemo(() => {
    if (selectedAnalysisId) {
      const selected = analyses.find((item) => getAnalysisId(item) === selectedAnalysisId);
      if (selected) return selected;
    }
    return analyses[0] ?? null;
  }, [analyses, selectedAnalysisId]);
  const activeAnalysisId = useMemo(() => getAnalysisId(analysis), [analysis]);
  const selectedChatSession = useMemo(() => {
    const sessionId = project?.CurrentAIChatSessionId || project?.currentAIChatSessionId;
    return sessionId
      ? {
          _id: sessionId,
          ReferenceId: migrationProjectId,
          ReferenceItemName: "MigrationProject",
        }
      : null;
  }, [migrationProjectId, project]);

  useEffect(() => {
    managerRef.current = {
      migrationProjectMgr,
      sourceSystemMgr,
      sourceObjectMgr,
      externalSystemMgr,
      entityObjectMgr,
    };
  }, [entityObjectMgr, externalSystemMgr, migrationProjectMgr, sourceObjectMgr, sourceSystemMgr]);

  const loadProject = useCallback(async () => {
    if (panelMode && !panelOpen) {
      return;
    }

    if (!migrationProjectId) {
      setError("Migration project id is required.");
      setLoading(false);
      return;
    }

    const loadKey = `${migrationProjectId}:${reloadKey}`;
    if (loadStateRef.current.inFlight && loadStateRef.current.key === loadKey) {
      return;
    }

    if (!loading && loadStateRef.current.key === loadKey) {
      return;
    }

    loadStateRef.current = { key: loadKey, inFlight: true };
    setLoading(true);
    setError("");
    try {
      const managers = managerRef.current;
      const projectResult = await managers.migrationProjectMgr.getItem(migrationProjectId);
      const nextProject = firstItem(projectResult);
      if (!nextProject) {
        throw new Error("Migration project was not found.");
      }

      setProject(nextProject);

      const sourceSystemId = getId(nextProject, "SourceSystemId", "sourceSystemId");
      const externalSystemId = getId(nextProject, "ExternalSystemId", "externalSystemId");

      const [sourceSystemResult, externalSystemResult] = await Promise.all([
        sourceSystemId ? managers.sourceSystemMgr.getItem(sourceSystemId) : Promise.resolve(null),
        externalSystemId ? managers.externalSystemMgr.getItem(externalSystemId) : Promise.resolve(null),
      ]);

      const nextSourceSystem = firstItem(sourceSystemResult);
      const nextExternalSystem = firstItem(externalSystemResult);
      setSourceSystem(nextSourceSystem);
      setExternalSystem(nextExternalSystem);

      const sourceObjectsResult = sourceSystemId
        ? await managers.sourceObjectMgr.Query({
            model: "x => x.SourceSystemId == sourceSystemId && x.IsActive == true && x.IsDeleted == false",
            params: { sourceSystemId },
          })
        : null;
      setSourceObjects(asArray(sourceObjectsResult?.Items));

      const schemaId = getId(nextExternalSystem, "SchemaId", "schemaId");
      const schemaVersionId = getId(nextExternalSystem, "ActiveSchemaVersionId", "activeSchemaVersionId");
      const entityObjectsResult = schemaId && schemaVersionId
        ? await managers.entityObjectMgr.Query({
            model: "x => x.SchemaId == schemaId && x.SchemaVersionId == schemaVersionId && x.IsActive == true && x.IsDeleted == false",
            params: { schemaId, schemaVersionId },
          })
        : null;
      setEntityObjects(asArray(entityObjectsResult?.Items));
    } catch (err) {
      setError(err?.message || "Failed to load migration project.");
    } finally {
      loadStateRef.current = { key: loadKey, inFlight: false };
      setLoading(false);
    }
  }, [loading, migrationProjectId, panelMode, panelOpen, reloadKey]);

  useEffect(() => {
    void loadProject();
  }, [loadProject]);

  useEffect(() => {
    if (!project) return;

    const defaultAnalysisId = getDefaultAnalysisId(project);
    const hasSelectedAnalysis = selectedAnalysisId && analyses.some((item) => getAnalysisId(item) === selectedAnalysisId);
    if (hasSelectedAnalysis && pendingSelectedAnalysisIdRef.current === selectedAnalysisId) {
      pendingSelectedAnalysisIdRef.current = "";
    }
    if (!hasSelectedAnalysis && pendingSelectedAnalysisIdRef.current === selectedAnalysisId) {
      return;
    }
    if (defaultAnalysisId && !hasSelectedAnalysis) {
      setSelectedAnalysisId(defaultAnalysisId);
    } else if (!defaultAnalysisId && selectedAnalysisId) {
      setSelectedAnalysisId("");
    }
  }, [analyses, project, selectedAnalysisId]);

  const handleRunAnalysis = async () => {
    if (!migrationProjectId || runningAnalysis) return;
    setRunningAnalysis(true);
    setAnalysisStatus("Reading scanned source metadata and destination schema, then asking AI for migration guidance.");
    setError("");
    try {
      const result = await migrationProjectMgr.RunOperation({
        operationName: "AIMigrationRunAnalysis",
        id: migrationProjectId,
        queryParams: { suppressPanelOpen: "true" },
      });

      setAnalysisStatus(resultMessage(result) || "Migration analysis completed.");

      const returnedProject = firstItem(result);
      if (returnedProject) {
        setProject(returnedProject);
        setSelectedAnalysisId(getDefaultAnalysisId(returnedProject));
      } else {
        const nextAnalysisId = getId(result?.Value || result?.value, "analysisId", "AnalysisId");
        if (nextAnalysisId) {
          pendingSelectedAnalysisIdRef.current = nextAnalysisId;
          setSelectedAnalysisId(nextAnalysisId);
        }
        setReloadKey((value) => value + 1);
      }
    } catch (err) {
      setAnalysisStatus("");
      setError(err?.message || "Failed to run migration analysis.");
    } finally {
      setRunningAnalysis(false);
    }
  };

  const handlePanelCancel = () => {
    if (panelMode && typeof onPanelClose === "function") {
      onPanelClose();
      return;
    }

    coreNavigate("/dynamic/migrationproject/");
  };

  const handlePanelUpdate = async (uiAction, item, value) => {
    void item;
    const normalizedAction = String(uiAction || "").toLowerCase();
    if (normalizedAction !== "migrationprojectupdated") return;

    const nextAnalysisId = getId(value, "analysisId", "AnalysisId");
    if (nextAnalysisId) {
      pendingSelectedAnalysisIdRef.current = nextAnalysisId;
      setSelectedAnalysisId(nextAnalysisId);
    }
    setAnalysisStatus(value?.message || value?.Message || "Migration analysis was updated from the approved plan.");
    setReloadKey((current) => current + 1);
  };

  const openRouteInNewWindow = (route) => {
    const target = String(route || "").trim();
    if (!target) return;

    if (typeof window === "undefined") {
      coreNavigate(target);
      return;
    }

    const url = new URL(target.startsWith("/") ? target : `/${target}`, window.location.origin).toString();
    const opened = window.open(url, "_blank");
    if (opened) {
      opened.opener = null;
    } else {
      coreNavigate(target);
    }
  };

  const navigateToSourceObject = (sourceObjectId) => {
    if (!sourceObjectId) return;
    const base = getRoutePath(getRoute("/convoke/sosearch/", "Convoke")) || "/convoke/sosearch/";
    openRouteInNewWindow(appendPathId(base, sourceObjectId));
  };

  const navigateToEntityObject = (entityObjectId) => {
    if (!entityObjectId) return;
    const base = getRoutePath(getRoute("/convoke/eosearch/", "Convoke")) || "/convoke/eosearch/";
    openRouteInNewWindow(appendPathId(base, entityObjectId));
  };

  const navigateToMappingDesigner = (entityObjectId, sourceObjectId) => {
    if (!entityObjectId || !sourceObjectId) return;
    const base = getRoutePath(getRoute("/entityobject/manage/", "Convoke")) || "/entityobject/manage/";
    openRouteInNewWindow(`${appendPathId(base, entityObjectId)}?sourceObjectId=${encodeURIComponent(sourceObjectId)}&allowMultiSource=true`);
  };

  const renderFindings = (findings, emptyText) => {
    const rows = asArray(findings);
    if (rows.length === 0) {
      return <div className="migration-project-empty">{emptyText}</div>;
    }

    return (
      <div className="migration-project-findings">
        {rows.map((finding, index) => (
          <div className="migration-project-finding" key={`${findingTitle(finding)}-${index}`}>
            <div className="migration-project-finding__header">
              <span className={`migration-project-severity migration-project-severity--${normalizeSeverity(findingSeverity(finding))}`}>
                {findingSeverity(finding)}
              </span>
              <span>{findingCategory(finding)}</span>
            </div>
            <div className="migration-project-finding__title">{findingTitle(finding)}</div>
            {findingDetail(finding) ? <div className="migration-project-finding__detail">{findingDetail(finding)}</div> : null}
            {findingRecommendation(finding) ? <div className="migration-project-finding__recommendation">{findingRecommendation(finding)}</div> : null}
          </div>
        ))}
      </div>
    );
  };

  const renderOverview = () => (
    <div className="migration-project-grid">
      <section className="migration-project-panel migration-project-panel--wide">
        <div className="migration-project-panel__title">Summary</div>
        <p>{analysis?.Summary || analysis?.summary || "Run analysis to generate migration readiness guidance."}</p>
      </section>
      <section className="migration-project-panel">
        <div className="migration-project-panel__title">Source</div>
        <div className="migration-project-stat">{sourceSystem?.Name || sourceSystem?.SystemName || "-"}</div>
        <div className="migration-project-muted">{sourceObjects.length} active source objects</div>
      </section>
      <section className="migration-project-panel">
        <div className="migration-project-panel__title">Destination</div>
        <div className="migration-project-stat">{externalSystem?.Name || externalSystem?.SystemName || "-"}</div>
        <div className="migration-project-muted">{entityObjects.length} active destination entities</div>
      </section>
      <section className="migration-project-panel">
        <div className="migration-project-panel__title">Mapping Candidates</div>
        <div className="migration-project-stat">{asArray(analysis?.EntityCandidates || analysis?.entityCandidates).length}</div>
        <div className="migration-project-muted">advisory matches</div>
      </section>
      <section className="migration-project-panel">
        <div className="migration-project-panel__title">Confidence</div>
        <div className="migration-project-stat">{formatPercent(analysis?.Confidence ?? analysis?.confidence)}</div>
        <div className="migration-project-muted">overall analysis</div>
      </section>
    </div>
  );

  const renderMappingCandidates = () => {
    const candidates = asArray(analysis?.EntityCandidates || analysis?.entityCandidates);
    const fieldRecommendations = asArray(analysis?.FieldMappingRecommendations || analysis?.fieldMappingRecommendations);
    const transformHints = asArray(analysis?.TransformationHints || analysis?.transformationHints);
    if (candidates.length === 0) {
      return (
        <div className="migration-project-stack">
          <div className="migration-project-empty">No mapping candidates have been generated.</div>
          {renderFieldRecommendations(fieldRecommendations)}
          {renderTransformHints(transformHints)}
        </div>
      );
    }

    return (
      <div className="migration-project-stack">
        <div className="migration-project-table-wrap">
          <table className="migration-project-table">
            <thead>
              <tr>
                <th>Source Object</th>
                <th>Destination Entity</th>
                <th>Confidence</th>
                <th>Coverage</th>
                <th>Rationale</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate) => {
                const sourceObjectId = candidate.SourceObjectId || candidate.sourceObjectId;
                const entityObjectId = candidate.EntityObjectId || candidate.entityObjectId;
                return (
                  <tr key={`${sourceObjectId}-${entityObjectId}`}>
                    <td>{candidate.SourceObjectName || candidate.sourceObjectName || sourceObjectId}</td>
                    <td>{candidate.EntityObjectName || candidate.entityObjectName || entityObjectId}</td>
                    <td>{formatPercent(candidate.Confidence ?? candidate.confidence)}</td>
                    <td>
                      {formatPercent(candidate.SourceCoverage ?? candidate.sourceCoverage)} / {formatPercent(candidate.DestinationCoverage ?? candidate.destinationCoverage)}
                    </td>
                    <td>
                      <div>{candidate.Rationale || candidate.rationale || "-"}</div>
                      {(candidate.TransformNotes || candidate.transformNotes) ? (
                        <div className="migration-project-muted">{candidate.TransformNotes || candidate.transformNotes}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="migration-project-actions">
                        <button type="button" onClick={() => navigateToSourceObject(sourceObjectId)}>Source</button>
                        <button type="button" onClick={() => navigateToEntityObject(entityObjectId)}>Entity</button>
                        <button type="button" onClick={() => navigateToMappingDesigner(entityObjectId, sourceObjectId)}>Map</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderFieldRecommendations(fieldRecommendations)}
        {renderTransformHints(transformHints)}
      </div>
    );
  };

  const renderFieldRecommendations = (recommendations) => {
    if (recommendations.length === 0) return null;

    return (
      <section className="migration-project-section">
        <div className="migration-project-section__title">Field Recommendations</div>
        <div className="migration-project-table-wrap">
          <table className="migration-project-table migration-project-table--compact">
            <thead>
              <tr>
                <th>Source Field</th>
                <th>Destination Field</th>
                <th>Confidence</th>
                <th>Rationale</th>
                <th>Transform</th>
              </tr>
            </thead>
            <tbody>
              {recommendations.map((item, index) => (
                <tr key={`${item.SourceObjectFieldId || item.sourceObjectFieldId}-${item.EntityObjectFieldId || item.entityObjectFieldId}-${index}`}>
                  <td>{item.SourceFieldName || item.sourceFieldName || item.SourceObjectFieldId || item.sourceObjectFieldId}</td>
                  <td>{item.EntityFieldName || item.entityFieldName || item.EntityObjectFieldId || item.entityObjectFieldId}</td>
                  <td>{formatPercent(item.Confidence ?? item.confidence)}</td>
                  <td>{item.Rationale || item.rationale || "-"}</td>
                  <td>{item.TransformHint || item.transformHint || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const renderTransformHints = (hints) => {
    if (hints.length === 0) return null;

    return (
      <section className="migration-project-section">
        <div className="migration-project-section__title">Transform Hints</div>
        <div className="migration-project-findings">
          {hints.map((hint, index) => (
            <div className="migration-project-finding" key={`${hint.Title || hint.title}-${index}`}>
              <div className="migration-project-finding__title">{hint.Title || hint.title || "Transform"}</div>
              <div className="migration-project-finding__detail">{hint.Detail || hint.detail || "-"}</div>
              {(hint.SuggestedTransform || hint.suggestedTransform) ? (
                <div className="migration-project-finding__recommendation">{hint.SuggestedTransform || hint.suggestedTransform}</div>
              ) : null}
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderActiveTab = (tabKey = activeTab) => {
    if (tabKey === "source") {
      return renderFindings(
        analysis?.SourceReadinessFindings || analysis?.sourceReadinessFindings,
        "No source requirements have been generated."
      );
    }
    if (tabKey === "destination") {
      return renderFindings(
        analysis?.DestinationConfigurationFindings || analysis?.destinationConfigurationFindings,
        "No destination readiness findings have been generated."
      );
    }
    if (tabKey === "mapping") {
      return renderMappingCandidates();
    }
    return renderOverview();
  };

  const migrationTabs = TABS.map((tab) => ({
    key: tab.key,
    title: tab.label,
    content: (
      <main className="migration-project-content">
        {renderActiveTab(tab.key)}
      </main>
    ),
  }));

  const handleMigrationTabChange = (nextIndexOrKey, tab, nextKey) => {
    const resolvedKey = typeof nextKey === "string"
      ? nextKey
      : typeof nextIndexOrKey === "number"
        ? TABS[nextIndexOrKey]?.key
        : typeof nextIndexOrKey === "string"
          ? nextIndexOrKey
          : tab?.key || nextIndexOrKey?.key;

    setActiveTab(resolvedKey || "overview");
  };

  const renderAdvisorPanel = (content) => (
    <AIPanel
      open={panelMode ? panelOpen : true}
      presentation={panelMode ? "slideout" : "inline"}
      mapToClass="core-layout"
      params={{ ...resolvedPanelParams, migrationProjectId, analysisId: activeAnalysisId }}
      referenceId={migrationProjectId}
      referenceName="MigrationProject"
      session={selectedChatSession}
      operationName="AIQuestionMigration"
      introMessage="Refine Migration Plan..."
      splashMessage="Ask me about this migration project."
      singleSessionMode={true}
      planningPreviewEnabled={false}
      onUpdatePanel={handlePanelUpdate}
      onCancel={handlePanelCancel}
    >
      {content}
    </AIPanel>
  );

  const renderNonChatPanel = (content) => {
    if (!panelMode) return content;

    return (
      <PanelControl
        open={panelOpen}
        onClose={handlePanelCancel}
        title="AI Migration Project"
        rootClassName="ai-panel"
        mapToClass="core-layout"
        position="right"
        minWidth={360}
        resizable={true}
        storageKey="ai-panel:width"
        sideBarClassName="side-navbar"
        contentClassName="ai-panel-content-scope"
      >
        <div className="migration-project-nonchat-panel">
          {content}
        </div>
      </PanelControl>
    );
  };

  if (loading) {
    return renderNonChatPanel(
      <div className="migration-project-screen migration-project-screen--loading">
        <Loading />
      </div>
    );
  }

  if (error && !project) {
    const errorContent = <div className="migration-project-screen migration-project-screen__error">{error}</div>;
    return renderNonChatPanel(errorContent);
  }

  return renderAdvisorPanel(
      <div className="migration-project-screen">
        <header className="migration-project-header">
          <div>
            <div className="migration-project-eyebrow">AI Migration Project</div>
            <h1>{project?.Name || "Migration Project"}</h1>
            <div className="migration-project-meta">
              <span>{sourceSystem?.Name || sourceSystem?.SystemName || "Source not selected"}</span>
              <span>{externalSystem?.Name || externalSystem?.SystemName || "Destination not selected"}</span>
              <span>{project?.Status || "Draft"}</span>
              <span>Analyzed {formatDate(project?.LastAnalyzedDate || project?.lastAnalyzedDate)}</span>
            </div>
          </div>
          <div className="migration-project-header-actions">
            {analyses.length > 0 ? (
              <label>
                <span>Analysis</span>
                <select
                  value={activeAnalysisId || ""}
                  onChange={(event) => setSelectedAnalysisId(event.target.value)}
                >
                  {analyses.map((item) => {
                    const itemId = getAnalysisId(item);
                    const version = item?.Version ?? item?.version ?? "-";
                    const sourceType = formatAnalysisSourceType(item?.SourceType || item?.sourceType);
                    const generatedDate = item?.GeneratedDate || item?.generatedDate;
                    return (
                      <option key={itemId} value={itemId}>
                        {`v${version} - ${sourceType} - ${formatDate(generatedDate)}`}
                      </option>
                    );
                  })}
                </select>
              </label>
            ) : null}
            <button
              type="button"
              className="migration-project-run"
              onClick={handleRunAnalysis}
              disabled={runningAnalysis}
              aria-label={runningAnalysis ? "Analyzing migration project" : "Run migration analysis"}
              title={runningAnalysis ? "Analyzing migration project" : "Run migration analysis"}
            >
              {runningAnalysis ? (
                <span className="migration-project-run__spinner" aria-hidden="true">
                  <Loading />
                </span>
              ) : (
                <IconControl iconKey="analytics" width="1rem" height="1rem" />
              )}
            </button>
          </div>
        </header>

        {analysisStatus ? <div className="migration-project-status">{analysisStatus}</div> : null}
        {error ? <div className="migration-project-warning">{error}</div> : null}

        <div className="migration-project-tab-shell">
          <TabPaneControl
            className="ai-panel-tabs migration-project-tabs"
            tabs={migrationTabs}
            activeIndex={Math.max(0, TABS.findIndex((tab) => tab.key === activeTab))}
            onChange={handleMigrationTabChange}
            compact={true}
            framed={false}
            unmountInactive={true}
          />
        </div>
      </div>
  );
};

export default MigrationProjectManage;
